import React, { useState, useEffect } from "react"
import Modal from "aldoo-ra/Modal"
import GridSummary from "aldoo-ra/GridSummary"
import Typography from "aldoo-ra/Typography"
import Badge from "aldoo-ra/Badge"
import Tabs from "aldoo-ra/Tabs"
import { api } from "aldoo-ra/API"
import { url } from "aldoo-ra/CMS/url"
import { priceDisplayFormat, getPaymentDuration } from "aldoo-ra/CMSModels/StorePrice/display-formatters"
import { renderPlatformIcon, getPlatformDisplayName } from "aldoo-ra/Admin/utils/platformIconUtils"
import moment from "moment"

/**
 * StoreProductInfoDialog - Read-only product information display
 *
 * Displays comprehensive product information in a friendly, organized layout using GridSummary.
 * Can be opened with either a product ID (pid) or MongoDB ObjectId (_id).
 *
 * @param {boolean} isOpen - Whether the dialog is open
 * @param {function} onClose - Callback when dialog closes
 * @param {string} pid - Product ID (pid field)
 * @param {string} productId - MongoDB ObjectId (_id field)
 */
export default function StoreProductInfoDialog({ isOpen, onClose, pid, productId }) {
  const [product, setProduct] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [activeTab, setActiveTab] = useState("licensing")

  useEffect(() => {
    const loadProduct = async () => {
      if (!isOpen || (!pid && !productId)) {
        return
      }

      setLoading(true)
      setError(null)

      try {
        const crudAPI = api("crud")
        const match = productId ? { _id: productId } : { pid }

        const response = await crudAPI({
          get: {
            model: "StoreProduct",
            match,
            limit: 1,
            populate: ["price", "licenseModel"]
          }
        })

        if (Array.isArray(response) && response.length > 0) {
          setProduct(response[0])
        } else {
          setError("Product not found")
        }
      } catch (err) {
        setError("Failed to load product information")
      } finally {
        setLoading(false)
      }
    }

    loadProduct()
  }, [isOpen, pid, productId])

  const getProductTypeColor = (type) => {
    const typeValue = typeof type === "object" ? type.value : type
    switch (typeValue) {
      case "subscription": return "text-admin_primary"
      case "consumable": return "text-admin_success"
      case "non-consumable": return "text-admin_warning"
      case "license": return "text-admin_info"
      default: return "text-admin_text"
    }
  }

  const getAvailabilityBadge = (availability) => {
    let availValue = availability
    if (Array.isArray(availability)) {
      availValue = availability[0]
    } else if (typeof availability === "object") {
      availValue = availability.value
    }

    switch (availValue) {
      case "in-stock": return <Badge variant="admin_success">In Stock</Badge>
      case "out-of-stock": return <Badge variant="admin_error">Out of Stock</Badge>
      case "pre-order": return <Badge variant="admin_warning">Pre-Order</Badge>
      case "discontinued": return <Badge variant="admin_default">Discontinued</Badge>
      default: return <Badge variant="admin_default">{availValue || "Unknown"}</Badge>
    }
  }

  const renderPriceModels = (price, productMeta) => {
    if (!price?.price_model || !Array.isArray(price.price_model)) return "No pricing"

    const metaIntervalValue = Array.isArray(productMeta)
      ? productMeta.find(m => m.key === "interval")?.value
      : productMeta?.interval

    return price.price_model.map((model, index) => {
      const isPrimary = model.primary
      const amount = priceDisplayFormat(model.amount, model.currency)
      const isModelOneTime = metaIntervalValue === "one-time" ||
        !model.duration || model.duration === "one-time-payment" || model.duration === 0 || model.duration === "0"
      const duration = isModelOneTime ? "One-time" : getPaymentDuration(model.duration, model.duration_in_days)?.replace(/^\/ /, "") || "One-time"
      const autoRenew = !isModelOneTime && model.auto_renew ? "Auto-renews" : null
      const trial = model.trial_duration ? `${model.trial_duration} days trial` : null

      return (
        <div key={index} className="mb-3 pb-3 border-b border-admin_bordered last:border-0 last:mb-0 last:pb-0">
          <div className="flex items-center gap-2 mb-3">
            {isPrimary && (
              <Badge 
                variant="admin_primary" 
                className="text-xs"
              >
                Primary
              </Badge>
            )}

            <Typography variant="admin_h4">
              {amount}
            </Typography>

            <Typography
              variant="admin_body_dim"
              className="my-0"
            >
              {isModelOneTime ? "One-time" : `/ ${duration}`}
            </Typography>
          </div>

          <div className="flex flex-col gap-1 text-sm text-admin_text mb-3">
            {autoRenew && <span>{autoRenew}</span>}

            {trial && <span>Trial: {trial}</span>}

            {model.requires_payment_method && (
              <span>Requires payment method</span>
            )}
          </div>

          {/* Platform-specific product IDs */}
          {model.real_product_id && model.real_product_id.length > 0 && (
            <div className="mt-2 pt-2 border-t border-admin_bordered/20">
              <Typography variant="admin_txt2_dim">
                Platform Product IDs:
              </Typography>
              
              {model.real_product_id.map((platformProduct, idx) => (
                <div key={idx} className="flex items-center gap-1 py-1">
                  {renderPlatformIcon(platformProduct.platform, "w-4 h-4 text-admin_text_secondary")}

                  <span className="text-admin_text_secondary">
                    {getPlatformDisplayName(platformProduct.platform)}:
                  </span>

                  <span className="text-admin_text font-mono ml-2">
                    {platformProduct.free ? "FREE" : platformProduct.id || "N/A"}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )
    })
  }

  const renderMetadata = (meta) => {
    if (!meta) return "No metadata"

    // Handle both array format [{key, value}] and object format {key: value}
    const metaEntries = Array.isArray(meta)
      ? meta
      : Object.entries(meta).map(([key, value]) => ({ key, value }))

    if (metaEntries.length === 0) return "No metadata"

    return (
      <div className="divide-y divide-admin_bordered">
        {metaEntries.map((item, index) => (
          <div key={index} className="flex justify-between items-center py-3">
            <Typography variant="admin_txt1_dim">
              {item.key}:
            </Typography>

            <Typography 
              variant="admin_body" 
              className="font-medium my-0"
            >
              {item.value}
            </Typography>
          </div>
        ))}
      </div>
    )
  }

  const renderContent = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center p-12">
          <Typography variant="admin_txt1_dim">
            Loading product information...
          </Typography>
        </div>
      )
    }

    if (error || !product) {
      return (
        <div className="flex items-center justify-center p-12">
          <Typography 
            variant="admin_txt1" 
            className="text-admin_error"
          >
            {error || "Product not found"}
          </Typography>
        </div>
      )
    }

    console.log("[StoreProductInfoDialog] Rendering product content")

    // Get primary price model for display
    const primaryPrice = product.price?.price_model?.find(p => p.primary) || product.price?.price_model?.[0]
    const priceAmount = primaryPrice ? priceDisplayFormat(primaryPrice.amount, primaryPrice.currency) : "No price"

    // Check price meta for one-time interval override (meta is on the price object)
    const priceMeta = product.price?.meta
    const metaInterval = Array.isArray(priceMeta)
      ? priceMeta.find(m => m.key === "interval")?.value
      : priceMeta?.interval

    const isOneTimePurchase = metaInterval === "one-time" || (primaryPrice && (
      primaryPrice.duration === "one-time-payment" ||
      primaryPrice.duration === undefined ||
      primaryPrice.duration === 0 ||
      primaryPrice.duration === "0"
    ))

    // Format duration - show "One-time" for purchases, formatted period for subscriptions
    let priceDuration = ""
    if (primaryPrice && !isOneTimePurchase) {
      priceDuration = getPaymentDuration(primaryPrice.duration, primaryPrice.duration_in_days)
    }

    // Get unique platforms linked with store
    const linkedPlatforms = [
      ...new Set(
        product.price?.price_model
          ?.filter((c) => c.type === "real")
          .map((c) => c.real_product_id?.map((p) => p.platform))
          .flat()
          .filter(Boolean) || []
      ),
    ].sort((a, b) => a.localeCompare(b))

    // Render as e-commerce product page style
    return (
      <div className="flex flex-col gap-6 pb-8">
        {/* Product Header Section - Image & Main Info */}
        <div className="flex gap-6">
          {/* Product Image & Platforms */}
          <div className="flex-shrink-0">
            <div className="mb-4">
              {(() => {
                const thumbnailUrl = typeof product.thumbnail === "string" ? product.thumbnail : product.thumbnail?.url
                return thumbnailUrl ? (
                  <img
                    src={url(thumbnailUrl)}
                    alt={product.name}
                    className="w-48 h-48 object-contain rounded-lg border border-admin_bordered bg-white mb-3"
                  />
                ) : (
                  <div className="w-48 h-48 flex items-center justify-center bg-admin_canvas rounded-lg border border-admin_bordered">
                    <Typography variant="admin_txt2_dim">
                      No image
                    </Typography>
                  </div>
                )
              })()}
            </div>

            {/* Platform Availability */}
            {linkedPlatforms.length > 0 && (
              <div>
                <Typography variant="admin_txt1_dim">
                  Available on:
                </Typography>

                <div className="flex gap-2 flex-wrap">
                  {linkedPlatforms.map((platform, idx) => (
                    <div key={idx} className="flex items-center gap-2 bg-admin_paper rounded-lg px-2 py-1 border border-admin_bordered">
                      {renderPlatformIcon(platform, "w-5 h-5 text-admin_neutral_3")}

                      <span className="text-admin_text text-sm font-medium">
                        {getPlatformDisplayName(platform)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Product Info */}
          <div className="flex-1">
            {/* Product Name */}
            <div className="flex items-center gap-3 mb-3">
              <Typography 
                component="h2"
                variant="admin_h4"
              >
                {product.name}
              </Typography>
            </div>

            {/* Status Badges */}
            <div className="flex items-center gap-2 mb-4">
              {(() => {
                const typeValue = typeof product.type === "object" ? product.type.value : product.type
                const typeName = typeof product.type === "object" ? product.type.name : product.type
                const variantMap = {
                  purchase: "admin_info",
                  subscription: "admin_primary",
                  licence: "admin_warning",
                  bundle: "admin_success"
                }
                return (
                  <Badge variant={variantMap[typeValue] || "admin_default"}>
                    {typeName}
                  </Badge>
                )
              })()}
              {product.enabled ? (
                <Badge variant="admin_success">Enabled</Badge>
              ) : (
                <Badge variant="admin_error">Disabled</Badge>
              )}
              {getAvailabilityBadge(product.availability)}
              {product.category && (
                <Badge variant="admin_default">{product.category}</Badge>
              )}
            </div>

            {/* Price Display */}
            <div className="bg-admin_canvas rounded-lg p-4 mb-4">
              <Typography 
                component="output"
                variant="admin_h2" 
                className="text-admin_primary"
              >
                {priceAmount} {priceDuration && <span className="text-admin_text_secondary text-base font-normal">
                  {priceDuration}
                </span>}
              </Typography>

              <div className="flex flex-col gap-1">
                {isOneTimePurchase && (
                  <Typography 
                    variant="admin_txt1_dim"
                    className="block mt-2"
                  >
                    One-time purchase
                  </Typography>
                )}

                {primaryPrice?.trial_duration > 0 && (
                  <div className="flex items-center gap-2 text-admin_success text-sm  mt-2">
                    <span>✓</span>
                    <span>{primaryPrice.trial_duration} days free trial</span>
                  </div>
                )}

                {!isOneTimePurchase && primaryPrice?.auto_renew === true && (
                  <div className="flex items-center gap-2 text-admin_text_secondary text-sm">
                    <span className="text-2xl mb-1">↻</span>
                    <span>Auto-renews</span>
                  </div>
                )}
              </div>
            </div>

            {/* Description */}
            <div className="mb-4">
              <Typography 
                variant="admin_body" 
                className="my-0"
              >
                {product.description || "No description available"}
              </Typography>
            </div>
          </div>
        </div>

        {/* Tabbed Details Section */}
        <Tabs
          variant="admin"
          value={activeTab}
          onChange={setActiveTab}
          tabs={[
            {
              value: "licensing",
              label: "Licensing"
            },
            {
              value: "identifiers",
              label: "Identifiers"
            },
            {
              value: "pricing",
              label: "Pricing"
            },
            {
              value: "details",
              label: "Details"
            },
            ...(product.meta && (Array.isArray(product.meta) ? product.meta.length > 0 : Object.keys(product.meta).length > 0) ? [{
              value: "metadata",
              label: "Metadata"
            }] : [])
          ]}
        />

        {/* Tab Content */}
        <div className="mt-4 h-[35vh] overflow-y-auto scrollbar-thin scrollbar-thumb-admin_neutral_2 scrollbar-track-admin_canvas">
          {activeTab === "licensing" && (
            <div className="divide-y divide-admin_bordered">
              {product.licenseModel && typeof product.licenseModel === "object" ? (
                <>
                  <div className="py-3">
                    <Typography variant="admin_txt2_dim">
                      Model
                    </Typography>

                    <Typography 
                      variant="admin_body" 
                      className="font-medium my-0"
                    >
                      {product.licenseModel.name}
                    </Typography>
                  </div>

                  <div className="py-3">
                    <Typography variant="admin_txt2_dim">
                      Code Pattern
                    </Typography>

                    <Typography 
                      variant="admin_body" 
                      className="font-mono my-0"
                    >
                      {product.licenseModel.codePattern}
                    </Typography>
                  </div>

                  <div className="py-3 grid grid-cols-2 gap-4">
                    <div>
                      <Typography variant="admin_txt2_dim">
                        Validity
                      </Typography>

                      <Typography 
                        variant="admin_body" 
                        className="my-0"
                      >
                        {product.licenseModel.validity ? `${product.licenseModel.validity} days` : "Unlimited"}
                      </Typography>
                    </div>

                    <div>
                      <Typography variant="admin_txt2_dim">
                        Seats
                      </Typography>

                      <Typography 
                        variant="admin_body" 
                        className="my-0"
                      >
                        {product.seats || "Unlimited"}
                      </Typography>
                    </div>
                  </div>

                  {product.supported_platforms && product.supported_platforms.length > 0 && (
                    <div className="py-3">
                      <Typography variant="admin_txt2_dim">
                        Supported Platforms
                      </Typography>

                      <div className="flex gap-2 flex-wrap mt-1">
                        {product.supported_platforms.map((platform, idx) => (
                          <div key={idx} className="flex items-center gap-1 bg-admin_canvas rounded px-2 py-1">
                            {renderPlatformIcon(platform, "w-5 h-5 text-admin_neutral_3 mr-1")}

                            <span className="text-admin_text text-sm">
                              {getPlatformDisplayName(platform)}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <Typography variant="admin_txt2_dim">
                  No license model configured
                </Typography>
              )}
            </div>
          )}

          {activeTab === "identifiers" && (
            <div className="divide-y divide-admin_bordered">
              <div className="py-3">
                <Typography variant="admin_txt2_dim">
                  Product ID
                </Typography>

                <Typography 
                  variant="admin_body" 
                  className="font-mono my-0"
                >
                  {product.pid}
                </Typography>
              </div>

              <div className="py-3">
                <Typography variant="admin_txt2_dim">
                  Price ID
                </Typography>

                <Typography 
                  variant="admin_body" 
                  className="font-mono my-0"
                >
                  {product.price?.price_id || "N/A"}
                </Typography>
              </div>
            </div>
          )}

          {activeTab === "pricing" && (
            <div>
              {renderPriceModels(product.price, product.price?.meta)}
            </div>
          )}

          {activeTab === "details" && (
            <div className="divide-y divide-admin_bordered">
              <div className="flex items-center justify-between py-3">
                <Typography variant="admin_txt1_dim">
                  Consumable:
                </Typography>

                <Typography 
                  variant="admin_body" 
                  className="my-0"
                >
                  {product.consumable ? "Yes" : "No"}
                </Typography>
              </div>

              <div className="flex items-center justify-between py-3">
                <Typography variant="admin_txt1_dim">
                  Created:
                </Typography>

                <Typography 
                  variant="admin_body" 
                  className="my-0"
                >
                  {product.created ? moment(product.created).format("MMM DD, YYYY HH:mm") : "N/A"}
                </Typography>
              </div>

              <div className="flex items-center justify-between py-3">
                <Typography variant="admin_txt1_dim">
                  Last Updated:
                </Typography>

                <Typography 
                  variant="admin_body" 
                  className="my-0"
                >
                  {product.updated ? moment(product.updated).format("MMM DD, YYYY HH:mm") : "N/A"}
                </Typography>
              </div>
            </div>
          )}

          {activeTab === "metadata" && product.meta && (Array.isArray(product.meta) ? product.meta.length > 0 : Object.keys(product.meta).length > 0) && (
            <div>
              {renderMetadata(product.meta)}
            </div>
          )}
        </div>
      </div>
    )
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Product Information"
      variant="admin_drawer_right"
      closeOnClickOutside={false}
      passInteractionEvents={true}
    >
      <div className="flex-1 h-full">
        <div className="p-6">
          {renderContent()}
        </div>
      </div>
    </Modal>
  )
}
